/**
 * Catalog metric_ids Gate 2 can score today. Keep in sync with
 * backend/src/gate2/ragMetrics.js.
 */
export const IMPLEMENTED_RAG_METRIC_IDS = Object.freeze([
  "rm_hit_at_k",
  "rm_mean_reciprocal_rank",
  "rm_context_entity_recall",
  "rm_retrieval_noise_rate",
  "rm_empty_retrieval_rate",
  "rm_document_attribution_accuracy",
  "rm_context_relevance_score",
  "de_contextual_precision",
  "de_contextual_recall",
  "rm_retrieval_success_rate",
  "rm_context_window_efficiency",
  "de_faithfulness",
  "de_answer_relevancy",
  "rm_hallucination_rate",
  "rm_hallucination_severity_index",
  "rm_grounded_answer_rate",
  "rm_citation_faithfulness",
  "rm_citation_coverage_rate",
  "rm_context_utilization_rate",
  "rm_answer_completeness_rate",
  "cm_semantic_similarity_score",
  "cm_pii_leakage_rate",
  "cm_factual_accuracy_rate",
  "rm_answer_correctness_at_reference",
  "rm_response_consistency_score",
  "rm_indirect_injection_resistance",
]);

/** Metrics that score empty retrieval as 0 instead of skipping the case. */
export const METRICS_ALLOW_EMPTY_RETRIEVAL = Object.freeze([
  "rm_hit_at_k",
  "rm_mean_reciprocal_rank",
  "rm_context_entity_recall",
  "rm_retrieval_noise_rate",
  "rm_empty_retrieval_rate",
]);

/** Metrics that include empty-outcome cases in the score payload. */
export const METRICS_NEED_ALL_OUTCOMES = Object.freeze(["rm_empty_retrieval_rate"]);

/** Retrieval-only metrics — do not send chunk text as actual_output. */
/** Chatbot / GEval metrics — score from answer + ground truth, not retrieval outcome alone. */
export const METRICS_ANSWER_JUDGE_IDS = Object.freeze([
  "cm_accuracy",
  "cm_f1_score",
  "cm_semantic_similarity_score",
  "cm_factual_accuracy_rate",
  "cm_abstention_correctness_rate",
  "rm_answer_correctness_at_reference",
]);

export const METRICS_NO_FAKE_ANSWER = Object.freeze([
  "rm_hit_at_k",
  "rm_mean_reciprocal_rank",
  "rm_context_entity_recall",
  "rm_retrieval_noise_rate",
  "rm_empty_retrieval_rate",
]);

export function resolveCatalogMetricId(item, metrics = []) {
  if (item.catalogMetricId) return item.catalogMetricId;
  const catalogIds = new Set(
    (item.cases || [])
      .map((tc) => metrics.find((m) => m.id === tc.metricId)?.metricId)
      .filter(Boolean)
  );
  if (catalogIds.size === 1) return Array.from(catalogIds)[0];
  const byName = metrics.find(
    (m) => m.metricName && item.name && m.metricName.toLowerCase() === item.name.toLowerCase()
  );
  return byName?.metricId || null;
}

export function isScorableOutcome(outcome, catalogMetricId) {
  if (METRICS_ANSWER_JUDGE_IDS.includes(catalogMetricId)) {
    return false;
  }
  if (METRICS_NEED_ALL_OUTCOMES.includes(catalogMetricId)) {
    return outcome === "retrieved" || outcome === "empty";
  }
  if (METRICS_ALLOW_EMPTY_RETRIEVAL.includes(catalogMetricId)) {
    return outcome === "retrieved" || outcome === "empty";
  }
  return outcome === "retrieved";
}

/** answer = Ask/actual_output; context = retrieved chunks. */
const RAG_EVIDENCE_NEEDS = Object.freeze({
  rm_hit_at_k: { answer: false, context: true },
  rm_mean_reciprocal_rank: { answer: false, context: true },
  rm_context_entity_recall: { answer: false, context: true },
  rm_retrieval_noise_rate: { answer: false, context: true },
  rm_empty_retrieval_rate: { answer: false, context: true },
  rm_ndcg_at_k: { answer: false, context: true },
  rm_context_relevance_score: { answer: false, context: true },
  de_contextual_precision: { answer: false, context: true },
  de_contextual_recall: { answer: false, context: true },
  rm_retrieval_success_rate: { answer: false, context: true },
  rm_context_window_efficiency: { answer: false, context: true },
  de_faithfulness: { answer: true, context: true },
  de_answer_relevancy: { answer: true, context: false },
  rm_hallucination_rate: { answer: true, context: true },
  rm_hallucination_severity_index: { answer: true, context: true },
  rm_grounded_answer_rate: { answer: true, context: true },
  rm_citation_faithfulness: { answer: true, context: true },
  rm_citation_coverage_rate: { answer: true, context: true },
  rm_context_utilization_rate: { answer: true, context: true },
  rm_answer_completeness_rate: { answer: true, context: false },
  cm_semantic_similarity_score: { answer: true, context: false },
  cm_pii_leakage_rate: { answer: true, context: false },
  cm_factual_accuracy_rate: { answer: true, context: false },
  rm_document_attribution_accuracy: { answer: true, context: false },
  rm_answer_correctness_at_reference: { answer: true, context: true },
  rm_response_consistency_score: { answer: true, context: false },
  rm_indirect_injection_resistance: { answer: true, context: true },
});

const RAG_EVIDENCE_LABELS = Object.freeze({
  rm_hit_at_k: "Hit at K",
  rm_mean_reciprocal_rank: "Mean Reciprocal Rank",
  rm_context_entity_recall: "Context Entity Recall",
  rm_retrieval_noise_rate: "Retrieval Noise Rate",
  rm_empty_retrieval_rate: "Empty Retrieval Rate",
  rm_ndcg_at_k: "NDCG@k",
  rm_context_relevance_score: "Context Relevance Score",
  de_contextual_precision: "Contextual Precision",
  de_contextual_recall: "Contextual Recall",
  rm_retrieval_success_rate: "Retrieval Success Rate",
  rm_context_window_efficiency: "Context Window Efficiency",
  de_faithfulness: "Faithfulness",
  de_answer_relevancy: "Answer Relevancy",
  rm_hallucination_rate: "Hallucination Rate",
  rm_hallucination_severity_index: "Hallucination Severity Index",
  rm_grounded_answer_rate: "Grounded Answer Rate",
  rm_citation_faithfulness: "Citation Faithfulness",
  rm_citation_coverage_rate: "Citation Coverage Rate",
  rm_context_utilization_rate: "Context Utilization Rate",
  rm_answer_completeness_rate: "Answer Completeness Rate",
  cm_semantic_similarity_score: "Semantic Similarity Score",
  cm_pii_leakage_rate: "PII Leakage Rate",
  cm_factual_accuracy_rate: "Factual Accuracy Rate",
  rm_document_attribution_accuracy: "Document Attribution Accuracy",
  rm_answer_correctness_at_reference: "Answer Correctness @Reference",
  rm_response_consistency_score: "Response Consistency Score",
  rm_indirect_injection_resistance: "Indirect Injection Resistance",
});

function normalizeEvidenceName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");
}

export function catalogIdFromMetricName(name) {
  const raw = String(name || "").trim();
  if (!raw) return null;
  if (RAG_EVIDENCE_NEEDS[raw]) return raw;
  const wanted = normalizeEvidenceName(raw);
  let bestId = null;
  let bestLen = -1;
  for (const [id, label] of Object.entries(RAG_EVIDENCE_LABELS)) {
    const n = normalizeEvidenceName(label);
    if (n === wanted || id === wanted) return id;
    if (wanted.includes(n) || n.includes(wanted)) {
      if (n.length > bestLen) {
        bestId = id;
        bestLen = n.length;
      }
    }
  }
  return bestId;
}

function resolveEvidenceMetricId(metricIdOrName) {
  const raw = String(metricIdOrName || "").trim();
  if (RAG_EVIDENCE_NEEDS[raw]) return raw;
  return catalogIdFromMetricName(raw) || raw;
}

export function metricNeedsGeneratedAnswer(metricIdOrName) {
  const id = resolveEvidenceMetricId(metricIdOrName);
  if (METRICS_ANSWER_JUDGE_IDS.includes(id)) return true;
  if (METRICS_NO_FAKE_ANSWER.includes(id)) return false;
  const spec = RAG_EVIDENCE_NEEDS[id];
  if (!spec) return true;
  return Boolean(spec.answer);
}

/** Ask API only when the catalog metric is known to score actual_output. */
export function metricNeedsAskApi(metricIdOrName) {
  const id = resolveEvidenceMetricId(metricIdOrName);
  if (!id) return false;
  if (METRICS_NO_FAKE_ANSWER.includes(id)) return false;
  if (METRICS_ANSWER_JUDGE_IDS.includes(id)) return true;
  const spec = RAG_EVIDENCE_NEEDS[id];
  return Boolean(spec?.answer);
}

export function metricNeedsRetrievedContext(metricIdOrName) {
  const id = resolveEvidenceMetricId(metricIdOrName);
  if (METRICS_NO_FAKE_ANSWER.includes(id) || id === "rm_empty_retrieval_rate" || id === "rm_ndcg_at_k") {
    return true;
  }
  const spec = RAG_EVIDENCE_NEEDS[id];
  if (!spec) return true;
  return Boolean(spec.context);
}
